/**
 * A call between two colleagues: the ring, its three endings, and the rule
 * that makes an unanswered one disappear.
 *
 * WHY THIS LIVES HERE AND A MEETING'S LIFECYCLE DOES NOT. A room opened
 * alongside something that already exists is that thing's business — it starts
 * and ends when the thing does, and this package only ever issues into it. A
 * call has no such owner. Nothing schedules it, nothing completes it, and the
 * moment it is over there is nothing left that cares. So the state machine has
 * to live somewhere, and the only honest somewhere is beside the rooms it
 * exists to open.
 *
 * WHAT A RING IS MADE OF IS NOT DECIDED HERE. `CallRegistry` is a port. This
 * file knows a call has two opaque parties, an instant it began ringing, and
 * one of four states; it does not know what a party is, where the ring is
 * stored, or what the storage calls those states. A caller supplies all three,
 * exactly as `roomKey(scope, id)` lets a caller supply a noun this package may
 * not contain.
 *
 * THERE IS NO SWEEPER, AND THAT IS THE DESIGN, not an omission. A ring nobody
 * answers is not marked missed by a background job; it simply stops being
 * answerable once `RING_MAX_AGE_MS` has passed, here and in the receiving
 * client alike. A missed call disappears. The cost of that is one row per
 * unanswered ring, left `ringing` forever; the cost of the alternative is a
 * scheduled process whose failure mode is calls that ring for an hour.
 *
 * THE RACES ARE PRICED, NOT DESIGNED OUT. Every transition below reads the
 * stored state, decides, then writes — so a decline landing between another
 * client's read and write can be overwritten. That is deliberate: the window is
 * one round trip on a record whose whole life is sixty seconds, the loser's
 * client re-reads within that second anyway, and a compare-and-swap would push
 * an `If-Match` requirement into the port for it. `RoomRegistry.put` is a CAS
 * because losing there costs two live rooms and two people who cannot see each
 * other; losing here costs a status that is wrong until the next poll.
 */
import type { JoinCredential } from "./room";

declare const callIdBrand: unique symbol;

/** A call's own id, whatever the storage underneath gives it. */
export type CallId = string & { readonly [callIdBrand]: "CallId" };

export function asCallId(raw: string): CallId {
  if (raw.length === 0) throw new Error("A call id may not be empty");
  return raw as CallId;
}

/**
 * `ringing` is the only live state. The other three are endings, and which one
 * a call reached is worth keeping: declined and abandoned look identical from
 * the outside and mean opposite things to the two people involved.
 */
export type CallStatus = "ringing" | "answered" | "declined" | "abandoned";

export interface Call {
  readonly id: CallId;
  /** Opaque identity of whoever placed it. Never interpreted here. */
  readonly from: string;
  /** Opaque identity of whoever it is ringing. */
  readonly to: string;
  /** When it started ringing — the instant the age rule measures from. */
  readonly ringingSince: Date;
  readonly status: CallStatus;
}

/** Where a ring is kept. A port: this package stores nothing itself. */
export interface CallRegistry {
  /** Record a new ring, `ringing`, and answer with it — including its new id. */
  place(from: string, to: string, ringingSince: Date): Promise<Call>;
  get(id: CallId): Promise<Call | undefined>;
  /** Move a call to `status`, unconditionally, and answer with the result. */
  setStatus(id: CallId, status: CallStatus): Promise<Call>;
}

/**
 * How a credential for a call's room is obtained.
 *
 * A port rather than `roomFor` reached for directly, because the caller has
 * things to do on the way — keying the room in its own vocabulary, choosing how
 * long it lives, and writing down that somebody entered it. Folding that behind
 * one method is what lets the state machine above own the whole ring without
 * this package learning any of it.
 */
export interface CallRooms {
  credentialFor(call: Call): Promise<JoinCredential>;
}

export interface CallDeps {
  calls: CallRegistry;
  rooms: CallRooms;
  /** Injectable so the age rule is testable without waiting a minute. */
  now?: () => Date;
}

/** A call could not do what was asked of it. The caller decides what that means. */
export class CallRefusedError extends Error {
  readonly code = "CALL_REFUSED" as const;
  constructor(message: string) {
    super(message);
    this.name = "CallRefusedError";
  }
}

/**
 * How long a ring stands. Long enough that someone can reach their laptop,
 * short enough that a ring arriving after a reconnect is obviously dead.
 *
 * Exported because the receiving client applies the same number — the rule has
 * to be one number in one place, or a client shows a ring the server will
 * refuse to connect.
 */
export const RING_MAX_AGE_MS = 60_000;

export function isRingStale(call: Call, now: Date): boolean {
  if (call.status !== "ringing") return false;
  return now.getTime() - call.ringingSince.getTime() > RING_MAX_AGE_MS;
}

function clockOf(deps: CallDeps): Date {
  return (deps.now ?? (() => new Date()))();
}

async function load(id: CallId, deps: CallDeps): Promise<Call> {
  const call = await deps.calls.get(id);
  if (!call) throw new CallRefusedError(`There is no call ${id}`);
  return call;
}

/**
 * Ring somebody, and go and wait in the room.
 *
 * The ring is recorded BEFORE the room is opened, so a room that cannot be
 * opened leaves a ring behind rather than the other way round. That ordering is
 * the cheaper failure: a stray ring is harmless — the age rule retires it in a
 * minute and nothing ever polls it again — whereas a room opened for a call
 * that was never recorded is a room the person being called cannot be told
 * about and nobody can audit.
 */
export async function startCall(
  from: string,
  to: string,
  deps: CallDeps,
): Promise<{ call: Call; credential: JoinCredential }> {
  if (from === to) throw new CallRefusedError("A party cannot ring itself");

  const call = await deps.calls.place(from, to, clockOf(deps));
  const credential = await deps.rooms.credentialFor(call);
  return { call, credential };
}

/**
 * Pick up.
 *
 * Answering an ALREADY-answered call is allowed and re-mints, which is not
 * laxity: a reload, a flaky network or a second device would otherwise lock
 * somebody out of a call they are already in, and refusing would be indistinguishable
 * to them from the other party hanging up. The age rule does not apply once a
 * call is answered — it guards the RING, and a long call has none left.
 */
export async function answerCall(id: CallId, deps: CallDeps): Promise<{ call: Call; credential: JoinCredential }> {
  const existing = await load(id, deps);

  if (existing.status !== "ringing" && existing.status !== "answered") {
    throw new CallRefusedError(`Call ${id} was ${existing.status} and cannot be answered`);
  }
  if (isRingStale(existing, clockOf(deps))) {
    throw new CallRefusedError(`Call ${id} rang out and is no longer answerable`);
  }

  // Marked answered before the room is opened, so the party who placed it stops
  // seeing a ring as early as possible. A mint that then fails is recoverable —
  // the branch above lets the same client try again — whereas a credential
  // handed out while the other end still shows "ringing" is not.
  const answered = existing.status === "answered" ? existing : await deps.calls.setStatus(id, "answered");
  const credential = await deps.rooms.credentialFor(answered);
  return { call: answered, credential };
}

/**
 * The two endings that open nothing, told apart because they mean opposite
 * things: `declineCall` is the party being rung saying no, `abandonCall` is the
 * party who rang giving up. Both are idempotent — a client retrying its own
 * settle is not an error — and neither may undo an answered call: leaving a
 * live room is not un-ringing it, and the room is left to the people in it.
 */
async function settle(
  id: CallId,
  target: Extract<CallStatus, "declined" | "abandoned">,
  deps: CallDeps,
): Promise<Call> {
  const existing = await load(id, deps);
  if (existing.status === target) return existing;
  if (existing.status !== "ringing") {
    throw new CallRefusedError(`Call ${id} was ${existing.status} and cannot be ${target}`);
  }
  // No staleness check: a client hanging up does not know its ring expired, and
  // refusing would leave it retrying something that can never succeed.
  return deps.calls.setStatus(id, target);
}

export function declineCall(id: CallId, deps: CallDeps): Promise<Call> {
  return settle(id, "declined", deps);
}

export function abandonCall(id: CallId, deps: CallDeps): Promise<Call> {
  return settle(id, "abandoned", deps);
}
